import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import moment, { tz } from 'moment';
import uuid from 'uuid';

import config from 'config/config';
import { setGlobalMessage } from 'containers/MessageHandler/actions';
import Analytics from 'utils/Analytics';
import checkOrSetSlash from 'utils/checkOrSetSlash';
import { customerIoTrackerWithParams } from 'utils/CustomerIo';
import { prepareFCHeader } from 'utils/request';

const rootProjectPath = `${checkOrSetSlash(config.apiHostGateway, 'apiHostGateway')}api/v1.1/`;

export const meetingsApi = createApi({
  reducerPath: 'meetingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: rootProjectPath,
    prepareHeaders: prepareFCHeader(),
  }),
  tagTypes: ['Meeting', 'MeetingsList', 'Stakeholders'],
  endpoints: builder => ({
    fetchMeeting: builder.query({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}`,
        method: 'GET',
      }),
      transformResponse: response => ({
        ...response,
        name: response?.description,
        meeting_start_time: response?.meeting_start_date,
        ...(response?.next_meeting_start_date && {
          next_meeting_start_time: response?.next_meeting_start_date,
        }),
      }),
      providesTags: (result, error, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
    }),
    fetchMeetings: builder.query({
      query: ({ projectId, searchValue, pagination }) => ({
        url: `projects/${projectId}/meetings/filter`,
        method: 'POST',
        body: {
          pagination,
          search_term: searchValue,
        },
      }),
      providesTags: ['MeetingsList'],
    }),
    createMeeting: builder.mutation({
      query: ({ projectId, moduleId, name, startDate }) => {
        const clientDate = new Date().toISOString();
        return {
          url: `projects/${projectId}/meetings/generate`,
          method: 'POST',
          body: {
            description: name,
            module_id: moduleId,
            meeting_start_date: startDate,
            client_created_at: clientDate,
            client_updated_at: clientDate,
          },
        };
      },
      invalidatesTags: ['MeetingsList'],
      async onQueryStarted({ projectId }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_creation_succeeded', { type: 'new' });
            customerIoTrackerWithParams('meetings_creation_succeeded', projectId, data.id);
          }
        } catch (error) {
          console.error('Error saving meeting:', error);
        }
      },
    }),
    duplicateMeeting: builder.mutation({
      query: ({ projectId, meetingId, startDate, description }) => {
        const clientDate = new Date().toISOString();
        return {
          url: `projects/${projectId}/meetings/${meetingId}/generate-next-meeting`,
          method: 'POST',
          body: {
            meeting_start_date: startDate,
            description,
            client_created_at: clientDate,
            client_updated_at: clientDate,
          },
        };
      },
      invalidatesTags: ['MeetingsList'],
      async onQueryStarted({ projectId }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            Analytics.track('meetings_creation_succeeded', { type: 'duplication' });
            customerIoTrackerWithParams('meetings_creation_succeeded', projectId, data.id);
          }
        } catch (error) {
          console.error('Error duplicating meeting:', error);
        }
      },
    }),
    deleteMeeting: builder.mutation({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Meeting', id: meetingId },
        'MeetingsList',
      ],
    }),
    updateDescription: builder.mutation({
      query: ({ projectId, meetingId, description }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/set-description`,
        method: 'PUT',
        body: {
          description,
          client_updated_at: new Date().toISOString(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Meeting', id: meetingId },
        'MeetingsList',
      ],
    }),
    updateMeetingDate: builder.mutation({
      query: ({ projectId, meetingId, meetingDate }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/set-meeting-date`,
        method: 'PUT',
        body: {
          meeting_start_date: meetingDate,
          meeting_end_date: new Date(
            new Date(meetingDate).getTime() + 2 * 60 * 60 * 1000,
          ).toISOString(),
          client_updated_at: new Date().toISOString(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Meeting', id: meetingId },
        'MeetingsList',
      ],
    }),
    updateNextMeetingDate: builder.mutation({
      query: ({ projectId, meetingId, nextMeetingDate }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/set-next-meeting-date`,
        method: 'PUT',
        body: {
          next_meeting_start_date: nextMeetingDate,
          next_meeting_end_date: new Date(
            new Date(nextMeetingDate).getTime() + 2 * 60 * 60 * 1000,
          ).toISOString(),
          client_updated_at: new Date().toISOString(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Meeting', id: meetingId },
        'MeetingsList',
      ],
    }),
    generateReport: builder.mutation({
      query: ({ projectId, meetingId }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/report/generate`,
        method: 'POST',
        body: {
          time_zone: tz.guess(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [{ type: 'Meeting', id: meetingId }],
      async onQueryStarted({ projectId, meetingId }, { dispatch, queryFulfilled }) {
        try {
          dispatch(
            meetingsApi.util.updateQueryData('fetchMeeting', { projectId, meetingId }, draft => {
              if (!draft) return draft;
              const updatedDraft = { ...draft };
              updatedDraft.export_status = 'Generating';
              return updatedDraft;
            }),
          );
          const { data } = await queryFulfilled;
          if (data && !data.message) {
            dispatch(
              setGlobalMessage({
                type: 'success',
                intlMessage: {
                  id: 'meeting_generation_alert',
                },
                active: true,
              }),
            );
            Analytics.track('reports_meetings_pdf_succeeded');
            customerIoTrackerWithParams('reports_meetings_pdf_succeeded', projectId, meetingId);
          }
        } catch (error) {
          console.error(error);
        }
      },
    }),
    fetchStakeholders: builder.query({
      query: ({ projectId, meetingId, filters = {}, limit = 1000, offset = 0 }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders/filter?limit=${limit}&offset=${offset}`,
        method: 'POST',
        body: filters,
      }),
      providesTags: ['Stakeholders'],
    }),
    createStakeholder: builder.mutation({
      query: ({ projectId, meetingId, stakeholder }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders`,
        method: 'POST',
        body: {
          ...stakeholder,
          id: uuid.v4(),
          user_id: stakeholder.user_id,
          attendance: stakeholder.attendance,
          is_attendee: true,
          is_summoned: true,
          is_recipient: false,
          client_updated_at: moment(),
          client_created_at: moment(),
        },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Stakeholders' },
        { type: 'Meeting', id: meetingId },
        { type: 'MeetingsList' },
      ],
    }),
    updateStakeholder: builder.mutation({
      query: ({ projectId, meetingId, stakeholder }) => ({
        url: `projects/${projectId}/meetings/${meetingId}/stakeholders/${stakeholder.id}`,
        method: 'PUT',
        body: { ...stakeholder, client_updated_at: moment() },
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Stakeholders' },
        { type: 'Meeting', id: meetingId },
        { type: 'MeetingsList' },
      ],
    }),
  }),
});

export const {
  useFetchMeetingQuery,
  useFetchMeetingsQuery,
  useCreateMeetingMutation,
  useDuplicateMeetingMutation,
  useDeleteMeetingMutation,
  useUpdateDescriptionMutation,
  useUpdateMeetingDateMutation,
  useUpdateNextMeetingDateMutation,
  useGenerateReportMutation,
  useFetchStakeholdersQuery,
  useCreateStakeholderMutation,
  useUpdateStakeholderMutation,
} = meetingsApi;
